/**
 * Chat List State Management
 * Replaces 10+ useState hooks with a single useReducer for better chat state coordination
 */

export interface ChatWindowState {
  isMinimized: boolean;
  isExpanded: boolean;
  isTyping: boolean;
  hasUnreadMessages: boolean;
  lastActivity: string;
  isLoading: boolean;
}

export interface OrderedChatStates {
  order: string[];
  states: Record<string, ChatWindowState>;
}

export interface FilterCounts {
  roleCounts: {
    all: number;
    talent: number;
    company: number;
    recruiter: number;
    internal: number;
  };
  typeCounts: {
    all: number;
    direct: number;
    group: number;
    channel: number;
  };
}

export interface UserRoles {
  companies: {
    id: string;
    name: string;
  }[];
  roles: string[];
}

export interface FloatingChatState {
  // UI State
  isExpanded: boolean;
  
  // Chat Windows Management
  activeChatStates: OrderedChatStates;
  
  // Online Status
  onlineUsers: Set<string>;
  
  // Filter State
  activeRoleFilters: Set<string>;
  activeTypeFilters: Set<string>;
  
  // Data State
  participantRoles: Record<string, string[]>;
  userRoles: UserRoles;
  
  
  // Group Expansion State
  expandedGroups: Set<string>;
  
  // Loading & Error State
  isLoading: boolean;
  error: string | null;
}

export type FloatingChatAction =
  | { type: 'SET_EXPANDED'; payload: boolean }
  | { type: 'OPEN_CHAT_WINDOW'; payload: { conversationId: string } }
  | { type: 'CLOSE_CHAT_WINDOW'; payload: string }
  | { type: 'MINIMIZE_CHAT_WINDOW'; payload: string }
  | { type: 'MAXIMIZE_CHAT_WINDOW'; payload: string }
  | { type: 'MOVE_TO_FIRST_POSITION'; payload: string }
  | { type: 'UPDATE_CHAT_STATE'; payload: { conversationId: string; updates: Partial<ChatWindowState> } }
  | { type: 'SET_ONLINE_USERS'; payload: Set<string> }
  | { type: 'ADD_ONLINE_USER'; payload: string }
  | { type: 'REMOVE_ONLINE_USER'; payload: string }
  | { type: 'TOGGLE_ROLE_FILTER'; payload: string }
  | { type: 'TOGGLE_TYPE_FILTER'; payload: string }
  | { type: 'RESET_ROLE_FILTERS' }
  | { type: 'RESET_TYPE_FILTERS' }
  | { type: 'SET_PARTICIPANT_ROLES'; payload: Record<string, string[]> }
  | { type: 'SET_USER_ROLES'; payload: UserRoles }
  | { type: 'TOGGLE_GROUP_EXPANSION'; payload: string }
  | { type: 'PRESERVE_GROUP_EXPANSION'; payload: Set<string> }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET_FILTERS' }
  | { type: 'RESTORE_PERSISTED_STATE'; payload: { conversationId: string; isMinimized: boolean; isExpanded: boolean }[] };

const initialChatWindowState: ChatWindowState = {
  isMinimized: false,
  isExpanded: false,
  isTyping: false,
  hasUnreadMessages: false,
  lastActivity: new Date().toISOString(),
  isLoading: false,
};

export const initialFloatingChatState: FloatingChatState = {
  isExpanded: false,
  activeChatStates: {
    order: [],
    states: {}
  },
  onlineUsers: new Set(),
  activeRoleFilters: new Set(['all']),
  activeTypeFilters: new Set(['all']),
  participantRoles: {},
  userRoles: {
    companies: [],
    roles: []
  },
  
  expandedGroups: new Set(),
  isLoading: false,
  error: null,
};

export function floatingChatReducer(
  state: FloatingChatState,
  action: FloatingChatAction
): FloatingChatState {
  switch (action.type) {
    case 'SET_EXPANDED':
      return {
        ...state,
        isExpanded: action.payload,
        error: null
      };

    case 'OPEN_CHAT_WINDOW': {
      const { conversationId } = action.payload;
      const existingState = state.activeChatStates.states[conversationId];
      
      // Create new chat state or update existing one
      const newChatState: ChatWindowState = existingState 
        ? { ...existingState, isMinimized: false }
        : { ...initialChatWindowState };

      // Move to first position and update state
      const newOrder = [conversationId, ...state.activeChatStates.order.filter(id => id !== conversationId)];

      return {
        ...state,
        activeChatStates: {
          order: newOrder,
          states: {
            ...state.activeChatStates.states,
            [conversationId]: newChatState
          }
        },
        error: null
      };
    }

    case 'CLOSE_CHAT_WINDOW': {
      const conversationId = action.payload;
      const newStates = { ...state.activeChatStates.states };
      delete newStates[conversationId];

      return {
        ...state,
        activeChatStates: {
          order: state.activeChatStates.order.filter(id => id !== conversationId),
          states: newStates
        },
        error: null
      };
    }

    case 'MINIMIZE_CHAT_WINDOW': {
      const conversationId = action.payload;
      const existingState = state.activeChatStates.states[conversationId];
      
      if (!existingState) return state;

      return {
        ...state,
        activeChatStates: {
          ...state.activeChatStates,
          states: {
            ...state.activeChatStates.states,
            [conversationId]: {
              ...existingState,
              isMinimized: true,
              isExpanded: false
            }
          }
        },
        error: null
      };
    }

    case 'MAXIMIZE_CHAT_WINDOW': {
      const conversationId = action.payload;
      const existingState = state.activeChatStates.states[conversationId];
      
      if (!existingState) return state;

      return {
        ...state,
        activeChatStates: {
          ...state.activeChatStates,
          states: {
            ...state.activeChatStates.states,
            [conversationId]: {
              ...existingState,
              isMinimized: false
            }
          }
        },
        error: null
      };
    }

    case 'MOVE_TO_FIRST_POSITION': {
      const conversationId = action.payload;
      const newOrder = [conversationId, ...state.activeChatStates.order.filter(id => id !== conversationId)];

      return {
        ...state,
        activeChatStates: {
          ...state.activeChatStates,
          order: newOrder
        },
        error: null
      };
    }

    case 'UPDATE_CHAT_STATE': {
      const { conversationId, updates } = action.payload;
      const existingState = state.activeChatStates.states[conversationId];
      
      if (!existingState) return state;

      return {
        ...state,
        activeChatStates: {
          ...state.activeChatStates,
          states: {
            ...state.activeChatStates.states,
            [conversationId]: {
              ...existingState,
              ...updates
            }
          }
        },
        error: null
      };
    }

    case 'SET_ONLINE_USERS':
      return {
        ...state,
        onlineUsers: action.payload,
        error: null
      };

    case 'ADD_ONLINE_USER': {
      const newOnlineUsers = new Set(state.onlineUsers);
      newOnlineUsers.add(action.payload);
      return {
        ...state,
        onlineUsers: newOnlineUsers,
        error: null
      };
    }

    case 'REMOVE_ONLINE_USER': {
      const newOnlineUsers = new Set(state.onlineUsers);
      newOnlineUsers.delete(action.payload);
      return {
        ...state,
        onlineUsers: newOnlineUsers,
        error: null
      };
    }

    case 'TOGGLE_ROLE_FILTER': {
      const role = action.payload;
      const newFilters = new Set(state.activeRoleFilters);
      
      if (role === 'all') {
        return {
          ...state,
          activeRoleFilters: new Set(['all']),
          error: null
        };
      }
      
      newFilters.delete('all');
      if (newFilters.has(role)) {
        newFilters.delete(role);
      } else {
        newFilters.add(role);
      }
      
      return {
        ...state,
        activeRoleFilters: newFilters.size === 0 ? new Set(['all']) : newFilters,
        error: null
      };
    }

    case 'TOGGLE_TYPE_FILTER': {
      const type = action.payload;
      const newFilters = new Set(state.activeTypeFilters);
      
      if (type === 'all') {
        return {
          ...state,
          activeTypeFilters: new Set(['all']),
          error: null
        };
      }
      
      newFilters.delete('all');
      if (newFilters.has(type)) {
        newFilters.delete(type);
      } else {
        newFilters.add(type);
      }
      
      return {
        ...state,
        activeTypeFilters: newFilters.size === 0 ? new Set(['all']) : newFilters,
        error: null
      };
    }

    case 'RESET_ROLE_FILTERS':
      return {
        ...state,
        activeRoleFilters: new Set(['all']),
        error: null
      };

    case 'RESET_TYPE_FILTERS':
      return {
        ...state,
        activeTypeFilters: new Set(['all']),
        error: null
      };

    case 'SET_PARTICIPANT_ROLES':
      return {
        ...state,
        participantRoles: action.payload,
        error: null
      };

    case 'SET_USER_ROLES':
      return {
        ...state,
        userRoles: action.payload,
        error: null
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };

    case 'TOGGLE_GROUP_EXPANSION': {
      const groupId = action.payload;
      const newExpandedGroups = new Set(state.expandedGroups);
      
      if (newExpandedGroups.has(groupId)) {
        newExpandedGroups.delete(groupId);
      } else {
        newExpandedGroups.add(groupId);
      }
      
      return {
        ...state,
        expandedGroups: newExpandedGroups,
        error: null
      };
    }

    case 'PRESERVE_GROUP_EXPANSION': {
      return {
        ...state,
        expandedGroups: action.payload,
        error: null
      };
    }

    case 'RESET_FILTERS':
      return {
        ...state,
        activeRoleFilters: new Set(['all']),
        activeTypeFilters: new Set(['all']),
        error: null
      };

    case 'RESTORE_PERSISTED_STATE': {
      const persistedStates = action.payload;
      const newOrder: string[] = [];
      const newStates: Record<string, ChatWindowState> = {};

      persistedStates.forEach(({ conversationId, isMinimized, isExpanded }) => {
        newOrder.push(conversationId);
        newStates[conversationId] = {
          ...initialChatWindowState,
          isMinimized,
          isExpanded,
          lastActivity: new Date().toISOString(),
        };
      });

      return {
        ...state,
        activeChatStates: {
          order: newOrder,
          states: newStates,
        },
      };
    }

    default:
      return state;
  }
}